// src/pages/Abonnement.jsx
import React, { useEffect, useState, useMemo } from "react";
import axiosInstance from "../axiosInstance";
import { notify } from "../ui/notify";

/**
 * Plans disponibles : même chose que dans routes/billingRoutes.js
 * (PLANS = { mensuel, annuel }).
 */
const PLAN_DEFS = {
  mensuel: {
    id: "mensuel",
    name: "Mensuel",
    amount: 25,
    periodLabel: "/mois",
    interval: "1 month",
    htNote: "HT",
    highlight: false,
    badge: null,
    equiv: null,
    features: [
      "Assistant IA pour générer vos devis",
      "Devis & factures en quelques secondes",
      "PDF propres, mentions légales automatiques",
      "Relances d’impayés par e-mail",
      "Clients & articles, suivi des marges",
      "Numérotation & conformité",
      "Export de vos données",
    ],
  },
  annuel: {
    id: "annuel",
    name: "Annuel",
    amount: 240,
    periodLabel: "/an",
    interval: "1 year",
    htNote: "HT",
    highlight: true,
    badge: "-20% en annuel",
    equiv: "≈ 20 €/mois",
    features: [
      "Toutes les fonctionnalités du plan mensuel",
      "Assistant IA inclus pour l’année",
      "Économisez 20 % sur 12 mois",
      "Support prioritaire e-mail",
    ],
  },
};

const PLANS = Object.values(PLAN_DEFS);

function formatDateFR(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function computeAccessUntil(start, intervalStr) {
  if (!start || !intervalStr) return null;
  const d = new Date(start);
  if (Number.isNaN(d.getTime())) return null;

  if (intervalStr === "1 month") {
    d.setMonth(d.getMonth() + 1);
    return d;
  }
  if (intervalStr === "1 year") {
    d.setFullYear(d.getFullYear() + 1);
    return d;
  }
  // autres intervalles éventuels : à compléter si besoin
  return null;
}

function formatIntervalHuman(intervalStr) {
  if (!intervalStr) return "";
  if (intervalStr === "1 month") return "1 mois";
  if (intervalStr === "1 year") return "1 an";
  return intervalStr;
}

export default function Abonnement() {
  const [status, setStatus] = useState({ status: "loading" });
  const [loadingPlan, setLoadingPlan] = useState(null); // id du plan en cours de redirection
  const [loadingCancel, setLoadingCancel] = useState(false);

  // 0 = rien, 1 = premier avertissement, 2 = confirmation finale
  const [cancelStep, setCancelStep] = useState(0);

  async function refresh() {
    try {
      const { data } = await axiosInstance.get("/billing/me");
      setStatus(data || { status: "none" });
    } catch {
      setStatus({ status: "none" });
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function start(planId) {
    if (!PLAN_DEFS[planId]) {
      notify.error({
        title: "Plan inconnu",
        message: "Plan sélectionné invalide.",
      });
      return;
    }
    try {
      setLoadingPlan(planId);
      const { data } = await axiosInstance.post("/billing/start", {
        plan_id: planId,
      });
      if (data?.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        notify.error({
          title: "Paiement",
          message: "Lien de paiement indisponible.",
        });
      }
    } catch (e) {
      const apiMessage =
        e?.response?.data?.message || "Impossible de démarrer l’abonnement.";
      notify.error({
        title: "Paiement",
        message: apiMessage,
      });
    } finally {
      setLoadingPlan(null);
    }
  }

  async function cancel() {
    try {
      setLoadingCancel(true);
      await axiosInstance.post("/billing/cancel");
      await refresh();
      setCancelStep(0); // on ferme le panneau de confirmation
      notify.success({
        title: "Abonnement",
        message:
          "Votre désabonnement est enregistré. Les prélèvements récurrents seront arrêtés.",
      });
    } catch {
      notify.error({
        title: "Abonnement",
        message: "Annulation impossible.",
      });
    } finally {
      setLoadingCancel(false);
    }
  }

  // style commun des boutons “pilule”
  const pillButtonBase =
    "inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-medium border shadow-sm " +
    "transition focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-sky-500";

  const isActive = status?.status === "active";
  const isPending = status?.status === "pending";
  const isCanceled = status?.status === "canceled";
  const isIncomplete = status?.status === "incomplete";

  const currentPlan = useMemo(
    () => (status?.plan_id ? PLAN_DEFS[status.plan_id] || null : null),
    [status?.plan_id]
  );

  const currentAmount = useMemo(() => {
    if (typeof status?.amount_cents === "number" && status.amount_cents > 0) {
      return status.amount_cents / 100;
    }
    if (currentPlan?.amount) return currentPlan.amount;
    return 0;
  }, [status?.amount_cents, currentPlan]);

  const statutLabel = useMemo(() => {
    switch (status?.status) {
      case "active":
        return "Actif";
      case "pending":
        return "En cours de validation";
      case "incomplete":
        return "À compléter";
      case "canceled":
        return "Annulé";
      case "none":
      default:
        return "Aucun abonnement";
    }
  }, [status?.status]);

  const statutColorClasses = useMemo(() => {
    if (isActive)
      return "bg-emerald-100 text-emerald-800 border border-emerald-200";
    if (isPending)
      return "bg-amber-100 text-amber-800 border border-amber-200";
    if (isIncomplete)
      return "bg-rose-100 text-rose-800 border border-rose-200";
    if (isCanceled)
      return "bg-slate-100 text-slate-700 border border-slate-200";
    return "bg-slate-100 text-slate-700 border-dashed border-slate-300";
  }, [isActive, isPending, isIncomplete, isCanceled]);

  const hasPlanWithIA = Boolean(currentPlan);

  // Date de fin de période payée (accès encore autorisé)
  const accessUntilDate = useMemo(() => {
    if (!status?.started_at || !status?.interval_str) return null;
    return computeAccessUntil(status.started_at, status.interval_str);
  }, [status?.started_at, status?.interval_str]);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-10">
      {/* En-tête */}
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold">Mon abonnement FlowDevis</h1>
        <p className="text-sm text-slate-600">
          Gérez ici votre formule, l’accès à l’assistant IA, les paiements
          récurrents et l’historique de facturation.
        </p>
      </header>

      {/* Bloc état actuel */}
      <section aria-labelledby="h2-etat-abonnement">
        <div className="rounded-2xl bg-white border shadow-sm p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h2 id="h2-etat-abonnement" className="text-base font-semibold">
                État de mon abonnement
              </h2>
              <span
                className={
                  "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium " +
                  statutColorClasses
                }
              >
                {statutLabel}
              </span>
            </div>

            {isActive || isPending || isIncomplete || isCanceled ? (
              <div className="text-sm text-slate-700 space-y-1">
                <div>
                  Plan&nbsp;:
                  <strong className="ml-1">
                    {currentPlan?.name || status?.plan_id || "—"}
                  </strong>
                  {currentAmount > 0 && (
                    <>
                      {" "}
                      —{" "}
                      <strong>
                        {currentAmount.toFixed(2)} €{" "}
                        <span className="font-normal text-slate-500">
                          ({formatIntervalHuman(status?.interval_str)})
                        </span>
                      </strong>
                    </>
                  )}
                </div>

                {status?.started_at && (
                  <div>
                    Démarré le&nbsp;
                    <strong>{formatDateFR(status.started_at)}</strong>
                  </div>
                )}

                {status?.canceled_at && (
                  <div>
                    Annulé le&nbsp;
                    <strong>{formatDateFR(status.canceled_at)}</strong>
                  </div>
                )}

                {isCanceled && accessUntilDate && (
                  <div>
                    Compte valable jusqu’au&nbsp;
                    <strong>{formatDateFR(accessUntilDate)}</strong>
                  </div>
                )}

                {isActive && hasPlanWithIA && (
                  <div className="text-xs text-emerald-700 mt-1">
                    L’assistant IA FlowDevis est inclus dans votre abonnement
                    pour un usage normal (création de devis assistée).
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-slate-600">
                Vous n’avez pas encore d’abonnement. Choisissez une formule
                ci-dessous pour activer FlowDevis et l’assistant IA.
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 items-stretch md:items-end">
            {isActive && (
              <button
                type="button"
                onClick={() => setCancelStep(1)}
                disabled={loadingCancel}
                className={
                  pillButtonBase +
                  " bg-white text-slate-700 border-slate-300 hover:bg-slate-50 " +
                  "disabled:opacity-60 disabled:cursor-not-allowed"
                }
              >
                {loadingCancel ? "Annulation…" : "Se désabonner"}
              </button>
            )}

            {!isActive && (
              <button
                type="button"
                onClick={refresh}
                className={
                  pillButtonBase +
                  " bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100"
                }
              >
                Actualiser le statut
              </button>
            )}
          </div>
        </div>

        {/* Panneau de confirmations successives */}
        {isActive && cancelStep > 0 && (
          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 space-y-3">
            {cancelStep === 1 && (
              <>
                <p className="text-sm font-semibold text-amber-900">
                  Attention, vous êtes en train de vous désabonner de FlowDevis.
                </p>
                <p className="text-xs text-amber-800">
                  En vous désabonnant, les prélèvements mensuels/annuels seront
                  arrêtés. Votre accès à l’application restera toutefois actif
                  jusqu’à la fin de votre période déjà payée.
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-md px-3 py-1.5 text-xs font-semibold bg-amber-700 text-white hover:bg-amber-600 transition"
                    onClick={() => setCancelStep(2)}
                  >
                    Continuer le désabonnement
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-md px-3 py-1.5 text-xs font-semibold bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 transition"
                    onClick={() => setCancelStep(0)}
                  >
                    Garder mon abonnement
                  </button>
                </div>
              </>
            )}

            {cancelStep === 2 && (
              <>
                <p className="text-sm font-semibold text-red-900">
                  Êtes-vous sûr de vouloir nous quitter&nbsp;?
                </p>
                <p className="text-xs text-red-800">
                  Cette action stoppera les prélèvements automatiques. Vous
                  conserverez l’accès jusqu’à la fin de la période déjà réglée,
                  puis votre compte sera bloqué automatiquement.
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-md px-3 py-1.5 text-xs font-semibold bg-red-600 text-white hover:bg-red-500 transition disabled:opacity-70"
                    onClick={cancel}
                    disabled={loadingCancel}
                  >
                    {loadingCancel
                      ? "Désabonnement en cours…"
                      : "Confirmer le désabonnement"}
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-md px-3 py-1.5 text-xs font-semibold bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 transition"
                    onClick={() => setCancelStep(0)}
                  >
                    Annuler et rester abonné
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </section>

      {/* TARIFS – style landing */}
      {!isActive && (
        <section id="tarifs" className="space-y-4" aria-labelledby="h2-tarifs">
          <div className="space-y-2 text-center">
            <h2 id="h2-tarifs" className="text-xl font-semibold">
              Tarifs simples et transparents — IA incluse
            </h2>
            <p className="text-sm text-slate-600">
              L’assistant IA FlowDevis est inclus dans vos formules pour un
              usage normal. Choisissez le rythme de facturation qui vous
              convient.
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-2 mt-4">
            {PLANS.map((p) => {
              const isHighlighted = Boolean(p.highlight);
              const isPlanLoading = loadingPlan === p.id;

              return (
                <article
                  key={p.id}
                  className={
                    "relative rounded-2xl border bg-white shadow-sm p-5 flex flex-col h-full " +
                    (isHighlighted
                      ? "border-sky-500 shadow-md ring-1 ring-sky-100"
                      : "border-slate-200")
                  }
                >
                  {isHighlighted && p.badge && (
                    <div className="absolute -top-3 left-4 inline-flex items-center rounded-full bg-sky-600 px-3 py-1 text-xs font-semibold text-white shadow">
                      {p.badge}
                    </div>
                  )}

                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-semibold">{p.name}</h3>
                      {p.equiv && (
                        <div className="text-xs text-slate-500 mt-1">
                          {p.equiv}
                        </div>
                      )}
                    </div>
                    <div className="text-right leading-tight">
                      <div className="inline-flex items-baseline">
                        <span className="text-3xl font-bold">{p.amount}</span>
                        <span className="ml-1 text-sm font-semibold">€</span>
                        <span className="ml-1 text-xs text-slate-500">
                          {p.periodLabel}
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {p.htNote}
                      </div>
                    </div>
                  </div>

                  <ul className="mt-2 space-y-1.5 text-sm text-slate-700 list-disc pl-5 flex-1">
                    {p.features.map((f, i) => (
                      <li key={i}>{f}</li>
                    ))}
                  </ul>

                  <div className="mt-5">
                    <button
                      type="button"
                      className={
                        "w-full h-10 rounded-md text-sm font-semibold transition " +
                        (isHighlighted
                          ? "bg-sky-700 text-white hover:bg-sky-600"
                          : "bg-slate-900 text-white hover:bg-slate-800")
                      }
                      disabled={Boolean(loadingPlan)}
                      onClick={() => start(p.id)}
                      aria-label={`Choisir l’offre ${p.name}`}
                    >
                      {isPlanLoading ? "Redirection…" : "Choisir ce plan"}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      )}

      {/* Facturation abonnement – à compléter plus tard */}
      <section aria-labelledby="h2-facturation" className="space-y-2">
        <h2 id="h2-facturation" className="text-base font-semibold">
          Facturation de l’abonnement
        </h2>
        <p className="text-sm text-slate-600">
          Les factures liées à votre abonnement (prélèvements Mollie) pourront
          être listées ici. Vous pourrez les télécharger au format PDF pour
          votre comptabilité.
        </p>

        <div className="mt-2 rounded-xl border border-dashed border-slate-300 bg-slate-50/60 px-4 py-3 text-xs text-slate-500">
          En cours de mise en place. Pour l’instant, retrouvez vos factures
          d’abonnement directement dans votre espace bancaire ou dans
          l’interface Mollie.
        </div>
      </section>
    </div>
  );
}
